import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { takeReturnTo } from "./RequireAuth";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

/**
 * The OIDC redirect landing route.
 *
 * 🪤🪤 THIS ROUTE MUST NOT REDIRECT ON ITS OWN. It used to be
 *
 *     <Route path="/callback" element={<Navigate to="/" replace />} />
 *
 * which produced an INFINITE "Signing in…" loop in production. `<Navigate>`
 * sits DEEPER in the tree than <AuthProvider>, and React runs effects
 * child-first, so the navigation stripped `?code=&state=` from the URL before
 * AuthProvider's signin-callback effect ever read it. The exchange therefore
 * never happened; `/` rendered RequireAuth, saw an unauthenticated user, and
 * called signinRedirect() again — and because ZITADEL still had a valid
 * session it answered instantly with a fresh code, straight back to here.
 *
 * 🔑 The loop is silent and fast: every hop is a legitimate 302, nothing
 * errors, and the only symptom is a spinner that never resolves. Nothing in
 * the network log looks wrong in isolation.
 *
 * So: wait for the exchange to finish, THEN navigate with the router (which
 * keeps react-router's own history in step, unlike a raw replaceState).
 */
export function Callback() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      // Return the operator to whatever they were trying to reach before
      // RequireAuth sent them to the front door. takeReturnTo() clears the
      // value and refuses anything that is not a same-app path.
      navigate(takeReturnTo(), { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  if (auth.error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Sign-in failed
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace" }}>
          {auth.error.message}
        </Typography>
        <Button variant="contained" onClick={() => void auth.signinRedirect()}>
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 4 }}>
      <CircularProgress size={20} />
      <Typography variant="body2">Completing sign-in…</Typography>
    </Box>
  );
}
